import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import "./AnnouncementPage.css";
import { Users } from 'lucide-react';
import { getFullNameByUserId } from "../../api/studentsAPI.js";
import CommitteeFilter from '../Common/CommitteeFilter.jsx';
import { getAnnouncementsByCommittee, getCommitteesByUser, getCommitteeFromId, deleteAnnouncement } from '../../api/announcementsAPI.js';
import { getGoogleCalendarEvents } from '../../api/calendarAPI.js';
import AnnouncementCard from './AnnouncementCard';
import { getDecryptedUserId } from '../../utils/cryptoUtils';

export default function AnnouncementPage() {
    const [search, setSearch] = useState("");
    const [announcements, setAnnouncements] = useState([]);
    const [committees, setCommittees] = useState([]);
    const [selectedCommittee, setSelectedCommittee] = useState("all");
    const [studentNames, setStudentNames] = useState({});
    const [committeeDetails, setCommitteeDetails] = useState({});
    const [showCalendarEvents, setShowCalendarEvents] = useState(true);
    const [isAscending, setIsAscending] = useState(false);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    // Load committees on component mount
    // Fix: Classify all under single useEffect since dependancies caused required ordered rendering
    useEffect(() => {
        const loadAllData = async () => {
            // differentiate btw loading and unresponsive
            setIsLoading(true);

            try {
                // load committees (decrypt from localStorage)
                const userId = await getDecryptedUserId();
                if (!userId) {
                    throw new Error("User id is invalid or does not exist.");
                }

                const committeeData = await getCommitteesByUser(userId);
                setCommittees(committeeData)

                if (committeeData.length === 0) {
                    setIsLoading(false);
                    return;
                }

                // Load announceemnt from all committees and calendar
                const announcementPromises = committeeData.map(committee =>
                    getAnnouncementsByCommittee(committee.committee_id)
                );

                const [announcementResponses, googleEvents] = await Promise.all([
                    Promise.all(announcementPromises),
                    getGoogleCalendarEvents()
                ])

                // Combine all announcements
                const allDbAnnouncements = announcementResponses.flatMap(response => response.data);
                const combinedAnnouncements = [...allDbAnnouncements, ...googleEvents];

                // Pre-fetch all required details before setting announcement
                const uniqueUserIds = [...new Set(allDbAnnouncements.map(a => a.created_by))];
                const uniqueCommitteeIds = [...new Set(allDbAnnouncements.map(a => a.committee))];

                // Fetch student names
                const studentNamePromises = uniqueUserIds.map(async (userId) => {
                    try {
                        const name = await getFullNameByUserId(userId);
                        return { userId, name };
                    } catch (error) {
                        console.error(`Error fetching student name for ${userId}:`, error);
                        return { userId, name: 'Unknown User' };
                    }
                })

                // Fetch committee details
                const committeePromises = uniqueCommitteeIds.map(async (committeeId) => {
                    try {
                        const committee = await getCommitteeFromId(committeeId);
                        return { committeeId, name: committee.name };
                    } catch (error) {
                        console.error(`Error fetching committee ${committeeId}:`, error);
                        return { committeeId, name: 'Unknown Committee' };
                    }
                })

                // Wait for all details to load to address dependancies
                const [studentResults, committeeResults] = await Promise.all([
                    Promise.all(studentNamePromises),
                    Promise.all(committeePromises)
                ])

                // Build lookup objects 
                const studentNamesMap = {};
                studentResults.forEach(({ userId, name }) => {
                    studentNamesMap[userId] = name;
                })

                const committeeDetailsMap = {};
                committeeResults.forEach(({ committeeId, name }) => {
                    committeeDetailsMap[committeeId] = name;
                })

                // Set everything here so there will be no race condition
                setStudentNames(studentNamesMap);
                setCommitteeDetails(committeeDetailsMap);
                setAnnouncements(combinedAnnouncements);
            } catch (error) {
                console.error("Failed to load data: ", error);
                alert("Failed to load announcements. Please refresh the page.")
            } finally {
                setIsLoading(false);
            }
        }
        loadAllData();
    }, [])

    const filteredAnnouncements = announcements.filter(announcement => {
        const q = search.trim().toLowerCase();
        // 1. Filter by Search
        const matchesSearch =
            (announcement.header || "").toLowerCase().includes(q) ||
            (announcement.message || "").toLowerCase().includes(q);

        // 2. Filter by Committee Selection
        let matchesCommittee = true;
        if (selectedCommittee !== "all") {
            if (announcement.source === 'GOOGLE_CALENDAR') {
                matchesCommittee = false;
            } else {
                matchesCommittee = announcement.committee === parseInt(selectedCommittee);
            }
        }

        // 3. Filter by Toggle
        const matchesToggle =
            announcement.source !== "GOOGLE_CALENDAR" || showCalendarEvents;

        return matchesSearch && matchesCommittee && matchesToggle;
    });

    const sortedAnnouncements = [...filteredAnnouncements].sort((a, b) => {
        const dateA = new Date(a.time_to_send);
        const dateB = new Date(b.time_to_send);
        return isAscending ? dateA - dateB : dateB - dateA;
    });

    const handleDelete = async (id, source) => {
        if (source === 'GOOGLE_CALENDAR') return;

        if (window.confirm("Are you sure you want to delete this announcement?")) {
            try {
                await deleteAnnouncement(id)
                setAnnouncements(announcements => announcements.filter(a => a.announcement_id !== id));
            } catch (error) {
                console.error("Error deleting announcement:", error);
                alert("Failed to delete announcement.");
            }
        }
    };

    return (
        <div className="main-container">
            {/* Header section */}
            <div className="header-container">
                <div>
                    <h1>Announcements</h1>
                    <p className="subtitle">Stay updated with the latest team news and updates</p>
                </div>
            </div>

            {/* Search section */}
            <div className="search-section">
                <input
                    className="search-input"
                    type="text"
                    placeholder="Search announcements..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />

                <CommitteeFilter
                    className='filter-select'
                    committees={committees}
                    value={selectedCommittee}
                    onChange={setSelectedCommittee}
                />

                {/* Toggle Button */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '0 8px',
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    border: '2px solid var(--color-border)',
                    whiteSpace: 'nowrap'
                }}>
                    <input
                        type="checkbox"
                        id="showCalendar"
                        checked={showCalendarEvents}
                        onChange={(e) => setShowCalendarEvents(e.target.checked)}
                    />
                    <label htmlFor="showCalendar">
                        Show Events
                    </label>
                </div>

                <button
                    className="btn"
                    onClick={() => setIsAscending(!isAscending)}
                    title={isAscending ? "Sort by Newest" : "Sort by Oldest"}
                >
                    {isAscending ? "↑ Oldest First" : "↓ Newest First"}
                </button>
            </div>

            {/* Broadcast section */}
            <div className="broadcast-card">
                <h3>
                    <Users size={24} />
                    Broadcast Message
                </h3>
                <p>
                    Send important updates to all team members instantly. Messages will be delivered via email and push notifications.
                </p>
                <button
                    className="broadcast-btn"
                    onClick={() => navigate("/push-message")}
                >
                    Send Broadcast
                </button>
            </div>

            {/* Announcements section */}
            <ul className="announcement-list">
                {isLoading ? (
                    <p className="loading-text">Loading announcements…</p>
                ) : sortedAnnouncements.length > 0 ? (
                    sortedAnnouncements.map((announcement, index) => {
                        const uniqueKey = announcement.source === "GOOGLE_CALENDAR"
                            ? announcement.id
                            : announcement.announcement_id;

                        return (
                            <AnnouncementCard
                                key={uniqueKey}
                                announcement={announcement}
                                onDelete={handleDelete}
                                committeeName={committeeDetails[announcement.committee]}
                                studentName={studentNames[announcement.created_by]}
                                index={index}
                            />
                        );
                    })
                ) : (
                    <p className="empty-state">
                        No announcements found. Try adjusting your filters.
                    </p>
                )}
            </ul>
        </div>
    )
}
